import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ContactInformation } from '../../model/contact-information';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DEFAULT_ALERT_CONFIG } from '../../../configuration/configuration.defaults';
import { formatAlertProgressLines } from '../../utils/alert-progress.util';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'patient-contact-element',
  templateUrl: './patient-contact-element.component.html',
  styleUrls: ['./patient-contact-element.component.scss'],
})
export class PatientContactElementComponent {
  @Input()
  subjectLabel = DEFAULT_ALERT_CONFIG.subjectLabel;

  @Input()
  contact?: ContactInformation;

  @Input()
  contactNumber?: string;

  @Input()
  providerSelected?: boolean;

  @Input()
  ongoing: boolean = false;

  @Input()
  ongoingEndTime?: number;

  @Input()
  initiatedOn?: number;

  @Input()
  followUps?: number;

  @Input()
  retryAttempts?: number;

  @Input()
  lastAttemptAt?: number;

  @Input()
  nextAlertAt?: number;

  @Input()
  scheduledEndAt?: number;

  @Input()
  sequenceId?: string;

  /** True while parent is calling cancel/stop API for this row. */
  @Input()
  stopping = false;

  @Output()
  notifyEvent: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  cancelNotification: EventEmitter<ContactInformation> = new EventEmitter<ContactInformation>();

  @Output()
  contactSelected: EventEmitter<ContactInformation> = new EventEmitter<ContactInformation>();

  @Output()
  editContact: EventEmitter<ContactInformation> = new EventEmitter<ContactInformation>();

  @Output()
  editPatient: EventEmitter<ContactInformation> = new EventEmitter<ContactInformation>();

  @Output()
  archivePatient: EventEmitter<ContactInformation> = new EventEmitter<ContactInformation>();

  @Output()
  deleteContact: EventEmitter<ContactInformation> = new EventEmitter<ContactInformation>();

  @Output()
  openAlertLog: EventEmitter<string> = new EventEmitter<string>();

  constructor(private snackBar: MatSnackBar) {}

  progressLines(): string[] {
    if (!this.ongoing || this.initiatedOn == null) {
      return [];
    }
    return formatAlertProgressLines({
      initiatedOn: this.initiatedOn,
      followUps: this.followUps ?? 0,
      retryAttempts: this.retryAttempts,
      lastAttemptAt: this.lastAttemptAt,
      nextAlertAt: this.nextAlertAt,
      scheduledEndAt: this.scheduledEndAt,
    });
  }

  canOpenAlertLog(): boolean {
    return !!this.contact?.selfPatient && this.ongoing && !!this.sequenceId;
  }

  onOpenAlertLog(): void {
    if (this.sequenceId && this.canOpenAlertLog()) {
      this.openAlertLog.emit(this.sequenceId);
    }
  }

  notify() {
    if (!this.providerSelected) {
      this.snackBar.open(
        'Set a callback location on this account before sending an alert.',
        'Okay',
      );
      return;
    }

    this.contactSelected.emit(this.contact);
    this.notifyEvent.emit();
  }

  edit() {
    this.editContact.emit(this.contact);
  }

  onEditPatient(): void {
    if (this.contact) {
      this.editPatient.emit(this.contact);
    }
  }

  onArchivePatient(): void {
    if (this.contact) {
      this.archivePatient.emit(this.contact);
    }
  }

  delete() {
    this.deleteContact.emit(this.contact);
  }

  cancelActiveAlert(): void {
    if (this.stopping || !this.contact) {
      return;
    }
    this.cancelNotification.emit(this.contact);
  }
}
